import { type ReactNode } from 'react';
import { Package, Receipt, User, Boxes, Wallet, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAbrirDetalle, type Entidad, type TipoDeEntidad } from './contexto-detalle';

/**
 * ENTITYLINK — nada que sea una cosa se muestra como texto muerto.
 *
 * Maciel lo pidió así: *«que una cosa me lleve a la otra»*. Un producto dentro
 * de una venta, un cliente dentro de una factura, una paca dentro de un
 * producto: todos son chips en los que se puede hacer clic, y abren el detalle
 * de esa cosa sin perder la pantalla de fondo.
 *
 * ═══ POR QUÉ NO ES UN `<Link>` ═══
 *
 * Un enlace normal cambia de pantalla y borra el contexto. Si estás mirando la
 * venta V-000012 y tocas el cliente, no quieres IRTE de la venta: quieres VER al
 * cliente un segundo y volver. Por eso esto abre el panel lateral y se apila.
 *
 * Fuera de un `ConDetalle` degrada a texto plano en vez de reventar: así se
 * puede reusar la misma tabla en un sitio sin panel (un PDF, un correo).
 */

const ICONO: Record<TipoDeEntidad, LucideIcon> = {
  producto: Package,
  venta: Receipt,
  cliente: User,
  paca: Boxes,
  gasto: Wallet,
};

export interface EntityLinkProps extends Entidad {
  /** Oculta el icono cuando el contexto ya lo deja claro (dentro de su propia columna). */
  sinIcono?: boolean;
  /** Monoespaciada: para códigos y números de venta. */
  mono?: boolean;
  className?: string;
  children?: ReactNode;
}

export function EntityLink({
  tipo,
  id,
  etiqueta,
  detalle,
  sinIcono,
  mono,
  className,
  children,
}: EntityLinkProps) {
  const abrir = useAbrirDetalle();
  const Icono = ICONO[tipo];
  const contenido = children ?? etiqueta;

  // Sin panel donde abrirlo, es texto. No un botón que no hace nada.
  if (!abrir) {
    return <span className={cn(mono && 'font-mono', className)}>{contenido}</span>;
  }

  return (
    <button
      type="button"
      title={detalle ? `${etiqueta} · ${detalle}` : etiqueta}
      onClick={(e) => {
        // Para que funcione dentro de una fila que también responde al clic.
        e.stopPropagation();
        abrir({ tipo, id, etiqueta, detalle });
      }}
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-left align-middle',
        'text-brand-700 transition-colors hover:bg-brand-50 hover:underline',
        'dark:text-brand-300 dark:hover:bg-brand-900/30',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        mono && 'font-mono text-xs',
        className,
      )}
    >
      {!sinIcono && <Icono className="h-3.5 w-3.5 shrink-0 opacity-70" />}
      <span className="truncate">{contenido}</span>
    </button>
  );
}
